import React, { useState, useEffect, useContext, useMemo } from 'react';
import { AuthContext } from '../App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Calendar, Plus, X, CheckCircle, AlertCircle, Car, MapPin, Clock, GripVertical, Home, Briefcase, Building, ChevronUp, ChevronDown, Navigation, Shield } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import LocationAutocomplete from '../components/LocationAutocomplete';
import LocationMap from '../components/LocationMap';

const ServiceRequestForm = () => {
  const { API, sessionToken } = useContext(AuthContext);
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [formData, setFormData] = useState({
    vehicle_id: '',
    service_type: '',
    description: '',
    urgency: 'normal'
  });
  
  // Estado para reclamo de seguro
  const [isInsuranceClaim, setIsInsuranceClaim] = useState(false);
  const [insuranceData, setInsuranceData] = useState({
    insurance_company: '',
    policy_number: '',
    claim_number: ''
  });
  
  // Lista de aseguradoras
  const insuranceCompanies = [
    { code: 'INS', name: 'Instituto Nacional de Seguros (INS)' },
    { code: 'MAPFRE', name: 'Mapfre Seguros' },
    { code: 'ASSA', name: 'ASSA Compañía de Seguros' },
    { code: 'LAFISE', name: 'Lafise Seguros' },
    { code: 'QUALITAS', name: 'Qualitas Seguros' },
    { code: 'OTRO', name: 'Otra aseguradora' }
  ];
  
  // Estado para múltiples fechas preferidas
  const [preferredDates, setPreferredDates] = useState([
    { id: 1, date: '', confirmed: false }
  ]);
  
  // Estado para ubicaciones preferidas
  const [preferredLocations, setPreferredLocations] = useState([
    { id: 1, type: '', name: '', address: '', priority: 1 }
  ]);

  // Tipos de ubicación predefinidos
  const locationTypes = [
    { value: 'home', label: 'Casa', icon: Home, color: 'text-green-600' },
    { value: 'work', label: 'Trabajo', icon: Briefcase, color: 'text-blue-600' },
    { value: 'other', label: 'Otro lugar', icon: Building, color: 'text-purple-600' }
  ];

  const serviceTypes = [
    'Cambio de aceite',
    'Cambio de frenos',
    'Cambio de llantas',
    'Alineación y balanceo',
    'Aire acondicionado',
    'Mecánica general',
    'Diagnóstico',
    'Revisión pre-RTV',
    'Enderezado y pintura',
    'Sistema eléctrico',
    'Otro'
  ];

  useEffect(() => {
    fetchVehicles();
  }, []);

  const fetchVehicles = async () => {
    try {
      const response = await axios.get(`${API}/vehicles`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      setVehicles(response.data);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
    }
  };

  // Memoized sorted locations for performance
  const sortedPreferredLocations = useMemo(() => {
    return [...preferredLocations].sort((a, b) => a.priority - b.priority);
  }, [preferredLocations]);

  // Función para agregar una nueva fecha
  const addDateOption = () => {
    if (preferredDates.length >= 3) {
      toast.error('Máximo 3 opciones de fecha permitidas');
      return;
    }
    
    setPreferredDates([
      ...preferredDates,
      { id: Date.now(), date: '', confirmed: false }
    ]);
  };

  // Función para eliminar una fecha
  const removeDateOption = (id) => {
    if (preferredDates.length <= 1) {
      toast.error('Debes tener al menos una fecha preferida');
      return;
    }
    setPreferredDates(preferredDates.filter(d => d.id !== id));
  };

  // Función para actualizar una fecha
  const updateDate = (id, newDate) => {
    setPreferredDates(preferredDates.map(d => 
      d.id === id ? { ...d, date: newDate, confirmed: false } : d
    ));
  };

  // Función para confirmar una fecha
  const confirmDate = (id) => {
    const dateToConfirm = preferredDates.find(d => d.id === id);
    
    if (!dateToConfirm?.date) {
      toast.error('Selecciona una fecha primero');
      return;
    }

    // Validar que la fecha sea futura
    const selectedDate = new Date(dateToConfirm.date);
    const now = new Date();
    
    if (selectedDate <= now) {
      toast.error('La fecha debe ser posterior a hoy');
      return;
    }

    // Confirmar la fecha
    setPreferredDates(preferredDates.map(d => 
      d.id === id ? { ...d, confirmed: true } : d
    ));

    // Mostrar mensaje de confirmación
    const formattedDate = selectedDate.toLocaleDateString('es-CR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });

    toast.success(
      <div className="flex items-start space-x-2">
        <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-medium">¡Fecha confirmada!</p>
          <p className="text-sm text-slate-600">{formattedDate}</p>
        </div>
      </div>,
      { duration: 4000 }
    );
  };

  // Formatear fecha para mostrar
  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-CR', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Verificar si hay al menos una fecha confirmada
  const hasConfirmedDate = preferredDates.some(d => d.confirmed && d.date);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (vehicles.length === 0) {
      toast.error('Debes agregar un vehículo primero');
      return;
    }

    // Validar que haya al menos una fecha confirmada
    if (!hasConfirmedDate) {
      toast.error('Debes confirmar al menos una fecha preferida');
      return;
    }

    try {
      // Filtrar solo las fechas confirmadas
      const confirmedDates = preferredDates
        .filter(d => d.confirmed && d.date)
        .map(d => new Date(d.date).toISOString());

      // Preparar ubicaciones ordenadas por prioridad
      const sortedLocations = preferredLocations
        .sort((a, b) => a.priority - b.priority)
        .filter(l => l.address) // Solo incluir ubicaciones con dirección
        .map(l => ({
          type: l.type,
          name: l.name,
          address: l.address,
          priority: l.priority,
          lat: l.lat,
          lng: l.lng
        }));

      // Validar que haya al menos una ubicación
      if (sortedLocations.length === 0) {
        toast.error('Debes agregar al menos una ubicación');
        return;
      }

      // Obtener la ubicación principal (primera)
      const primaryLocation = sortedLocations[0];

      const requestData = {
        vehicle_id: formData.vehicle_id,
        service_type: formData.service_type,
        description: formData.description,
        urgency: formData.urgency,
        preferred_dates: confirmedDates,
        preferred_date: confirmedDates[0], // Primera fecha como principal (compatibilidad)
        preferred_locations: sortedLocations,
        location: {
          address: primaryLocation?.address || '',
          city: primaryLocation?.name || '',
          lat: primaryLocation?.lat || null,
          lng: primaryLocation?.lng || null
        },
        // Insurance claim fields
        is_insurance_claim: isInsuranceClaim,
        ...(isInsuranceClaim && {
          insurance_company: insuranceData.insurance_company,
          policy_number: insuranceData.policy_number || null,
          claim_number: insuranceData.claim_number || null
        })
      };

      await axios.post(`${API}/service-requests`, requestData, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });

      toast.success(
        <div className="flex items-start space-x-2">
          <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium">¡Solicitud publicada!</p>
            <p className="text-sm text-slate-600">Los talleres cercanos podrán ver tu solicitud y enviarte ofertas.</p>
          </div>
        </div>,
        { duration: 5000 }
      );
      
      navigate('/client/dashboard');
    } catch (error) {
      toast.error('Error al crear solicitud');
      console.error(error);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="container mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center space-x-4 mb-8">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/client/dashboard')} 
            className="hover:bg-purple-100"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
              Solicitar Servicio
            </h1>
            <p className="text-slate-600">Publica tu necesidad y recibe ofertas de talleres cercanos</p>
          </div>
        </div>

        <Card className="max-w-3xl mx-auto border-slate-200">
          {vehicles.length === 0 ? (
            <div className="text-center py-12 px-8">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Car className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">No tienes vehículos registrados</h3>
              <p className="text-slate-600 mb-6">Debes agregar un vehículo antes de solicitar servicios</p>
              <Button 
                onClick={() => navigate('/client/vehicles')} 
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="goto-vehicles-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Agregar Vehículo
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="p-8 space-y-6">
              {/* Vehículo */}
              <div>
                <Label className="text-slate-700 font-medium flex items-center mb-2">
                  <Car className="w-4 h-4 mr-2 text-purple-600" />
                  Vehículo
                </Label>
                <Select onValueChange={(value) => setFormData({ ...formData, vehicle_id: value })} required>
                  <SelectTrigger data-testid="vehicle-select" className="border-purple-200 focus:border-purple-500">
                    <SelectValue placeholder="Selecciona un vehículo" />
                  </SelectTrigger>
                  <SelectContent>
                    {vehicles.map((vehicle) => (
                      <SelectItem key={vehicle.id} value={vehicle.id}>
                        {vehicle.make} {vehicle.model} ({vehicle.license_plate})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Tipo de Servicio */}
              <div>
                <Label className="text-slate-700 font-medium mb-2">Tipo de Servicio</Label>
                <Select onValueChange={(value) => setFormData({ ...formData, service_type: value })} required>
                  <SelectTrigger data-testid="service-type-select" className="border-purple-200 focus:border-purple-500">
                    <SelectValue placeholder="Selecciona el servicio" />
                  </SelectTrigger>
                  <SelectContent>
                    {serviceTypes.map((service) => (
                      <SelectItem key={service} value={service}>
                        {service}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Descripción */}
              <div>
                <Label className="text-slate-700 font-medium mb-2">Descripción del Problema</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe detalladamente el problema o servicio requerido..."
                  rows={4}
                  required
                  className="border-purple-200 focus:border-purple-500"
                  data-testid="description-textarea"
                />
              </div>

              {/* Urgencia */}
              <div>
                <Label className="text-slate-700 font-medium flex items-center mb-2">
                  <Clock className="w-4 h-4 mr-2 text-purple-600" />
                  Urgencia
                </Label>
                <Select value={formData.urgency} onValueChange={(value) => setFormData({ ...formData, urgency: value })}>
                  <SelectTrigger data-testid="urgency-select" className="border-purple-200 focus:border-purple-500">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="urgent">
                      <span className="flex items-center">
                        <span className="w-2 h-2 bg-red-500 rounded-full mr-2"></span>
                        Urgente - Lo antes posible
                      </span>
                    </SelectItem>
                    <SelectItem value="normal">
                      <span className="flex items-center">
                        <span className="w-2 h-2 bg-yellow-500 rounded-full mr-2"></span>
                        Normal - Esta semana
                      </span>
                    </SelectItem>
                    <SelectItem value="flexible">
                      <span className="flex items-center">
                        <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                        Flexible - Cuando haya disponibilidad
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Reclamo de Seguro */}
              <div className="border border-blue-200 rounded-lg p-5 bg-blue-50/50">
                <div className="flex items-start space-x-3">
                  <div className="flex items-center h-6">
                    <input
                      type="checkbox"
                      id="insurance-claim"
                      checked={isInsuranceClaim}
                      onChange={(e) => setIsInsuranceClaim(e.target.checked)}
                      className="w-5 h-5 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                      data-testid="insurance-claim-checkbox"
                    />
                  </div>
                  <div className="flex-1">
                    <Label htmlFor="insurance-claim" className="text-slate-700 font-medium flex items-center cursor-pointer">
                      <Shield className="w-4 h-4 mr-2 text-blue-600" />
                      Este es un reclamo de seguro
                    </Label>
                    <p className="text-sm text-slate-500 mt-1">
                      Marca esta opción si el servicio será cubierto por una aseguradora. El taller realizará la valuación de daños.
                    </p>
                  </div>
                </div>

                {/* Campos de seguro (visibles solo si está marcado) */}
                {isInsuranceClaim && (
                  <div className="mt-4 pt-4 border-t border-blue-200 space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-700 font-medium mb-2">Aseguradora *</Label>
                        <Select 
                          value={insuranceData.insurance_company} 
                          onValueChange={(value) => setInsuranceData({ ...insuranceData, insurance_company: value })}
                        >
                          <SelectTrigger data-testid="insurance-company-select" className="border-blue-200 focus:border-blue-500 bg-white">
                            <SelectValue placeholder="Selecciona tu aseguradora" />
                          </SelectTrigger>
                          <SelectContent>
                            {insuranceCompanies.map((company) => (
                              <SelectItem key={company.code} value={company.code}>
                                {company.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-slate-700 font-medium mb-2">Número de Póliza</Label>
                        <Input
                          value={insuranceData.policy_number}
                          onChange={(e) => setInsuranceData({ ...insuranceData, policy_number: e.target.value })}
                          placeholder="Ej: POL-123456"
                          className="border-blue-200 focus:border-blue-500 bg-white"
                          data-testid="policy-number-input"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-slate-700 font-medium mb-2">Número de Reclamo/Siniestro</Label>
                      <Input
                        value={insuranceData.claim_number}
                        onChange={(e) => setInsuranceData({ ...insuranceData, claim_number: e.target.value })}
                        placeholder="Ej: SIN-2026-001234 (si ya lo tienes)"
                        className="border-blue-200 focus:border-blue-500 bg-white"
                        data-testid="claim-number-input"
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        Si aún no tienes número de siniestro, el taller puede ayudarte a gestionarlo.
                      </p>
                    </div>
                    <div className="bg-blue-100 rounded-lg p-3 flex items-start space-x-2">
                      <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-blue-800">
                        <strong>Proceso de seguro:</strong> El taller seleccionado realizará una valuación de daños que será enviada a tu aseguradora para aprobación.
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Fechas Preferidas - NUEVA SECCIÓN */}
              <div className="border border-purple-200 rounded-lg p-5 bg-purple-50/50">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <Label className="text-slate-700 font-medium flex items-center">
                      <Calendar className="w-4 h-4 mr-2 text-purple-600" />
                      Fechas Preferidas
                      <Badge className="ml-2 bg-purple-100 text-purple-700 text-xs">
                        Mínimo 1 - Máximo 3
                      </Badge>
                    </Label>
                    <p className="text-sm text-slate-500 mt-1">
                      Selecciona hasta 3 opciones de fecha y confirma cada una
                    </p>
                  </div>
                  {preferredDates.length < 3 && (
                    <Button 
                      type="button"
                      variant="outline" 
                      size="sm"
                      onClick={addDateOption}
                      className="border-purple-300 text-purple-600 hover:bg-purple-100"
                      data-testid="add-date-btn"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Agregar Opción
                    </Button>
                  )}
                </div>

                <div className="space-y-3">
                  {preferredDates.map((dateOption, index) => (
                    <div 
                      key={dateOption.id} 
                      className={`flex items-center space-x-3 p-3 rounded-lg transition-all ${
                        dateOption.confirmed 
                          ? 'bg-green-50 border border-green-200' 
                          : 'bg-white border border-slate-200'
                      }`}
                    >
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                        <span className="text-sm font-medium text-purple-600">{index + 1}</span>
                      </div>
                      
                      <div className="flex-1">
                        <Input
                          type="datetime-local"
                          value={dateOption.date}
                          onChange={(e) => updateDate(dateOption.id, e.target.value)}
                          className={`border-purple-200 focus:border-purple-500 ${
                            dateOption.confirmed ? 'bg-green-50' : ''
                          }`}
                          disabled={dateOption.confirmed}
                          data-testid={`preferred-date-input-${index}`}
                        />
                      </div>

                      {dateOption.confirmed ? (
                        <div className="flex items-center space-x-2">
                          <Badge className="bg-green-100 text-green-700 flex items-center">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Confirmada
                          </Badge>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setPreferredDates(preferredDates.map(d => 
                              d.id === dateOption.id ? { ...d, confirmed: false } : d
                            ))}
                            className="text-slate-500 hover:text-slate-700"
                          >
                            Editar
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center space-x-2">
                          <Button
                            type="button"
                            size="sm"
                            onClick={() => confirmDate(dateOption.id)}
                            className="bg-green-600 hover:bg-green-700"
                            disabled={!dateOption.date}
                            data-testid={`confirm-date-btn-${index}`}
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Confirmar
                          </Button>
                          
                          {preferredDates.length > 1 && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeDateOption(dateOption.id)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              data-testid={`remove-date-btn-${index}`}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Indicador de estado */}
                <div className={`mt-4 p-3 rounded-lg flex items-center space-x-2 ${
                  hasConfirmedDate 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {hasConfirmedDate ? (
                    <>
                      <CheckCircle className="w-5 h-5" />
                      <span className="text-sm font-medium">
                        {preferredDates.filter(d => d.confirmed).length} fecha(s) confirmada(s) - ¡Listo para enviar!
                      </span>
                    </>
                  ) : (
                    <>
                      <AlertCircle className="w-5 h-5" />
                      <span className="text-sm font-medium">
                        Confirma al menos una fecha para continuar
                      </span>
                    </>
                  )}
                </div>
              </div>

              {/* Ubicaciones Preferidas - NUEVA SECCIÓN */}
              <div className="border border-purple-200 rounded-lg p-5 bg-purple-50/50">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <Label className="text-slate-700 font-medium flex items-center">
                      <MapPin className="w-4 h-4 mr-2 text-purple-600" />
                      Ubicaciones de Preferencia
                      <Badge className="ml-2 bg-purple-100 text-purple-700 text-xs">
                        Ordena por prioridad
                      </Badge>
                    </Label>
                  </div>
                  {preferredLocations.length < 3 && (
                    <Button 
                      type="button"
                      variant="outline" 
                      size="sm"
                      onClick={() => setPreferredLocations([
                        ...preferredLocations,
                        { id: Date.now(), type: '', name: '', address: '', priority: preferredLocations.length + 1 }
                      ])}
                      className="border-purple-300 text-purple-600 hover:bg-purple-100"
                      data-testid="add-location-btn"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Agregar Ubicación
                    </Button>
                  )}
                </div>
                
                <p className="text-sm text-slate-500 mb-4">
                  Indica dónde te queda más cómodo recibir el servicio. Puedes agregar hasta 3 ubicaciones y ordenarlas por preferencia.
                </p>

                {/* Ejemplo guía */}
                <div className="bg-white/70 rounded-lg p-3 mb-4 border border-slate-200">
                  <p className="text-xs text-purple-700 font-medium mb-1">💡 Ejemplo:</p>
                  <p className="text-xs text-slate-600">
                    <span className="font-medium">1. Casa:</span> "Residencial Los Laureles, 200m norte del parque, Heredia" <br/>
                    <span className="font-medium">2. Trabajo:</span> "Edificio Corporativo ABC, Sabana Norte, San José" <br/>
                    <span className="font-medium">3. Otro:</span> "Centro Comercial Multiplaza, cerca de entrada principal"
                  </p>
                </div>

                <div className="space-y-3">
                  {sortedPreferredLocations.map((location, index) => {
                      const LocationIcon = locationTypes.find(t => t.value === location.type)?.icon || MapPin;
                      const iconColor = locationTypes.find(t => t.value === location.type)?.color || 'text-slate-600';
                      
                      return (
                        <div 
                          key={location.id} 
                          className="bg-white rounded-lg p-4 border border-slate-200 hover:border-purple-300 transition-colors"
                        >
                          <div className="flex items-start space-x-3">
                            {/* Número de prioridad con flechas */}
                            <div className="flex flex-col items-center space-y-1">
                              <Badge className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                index === 0 ? 'bg-yellow-100 text-yellow-700' : 'bg-slate-100 text-slate-600'
                              }`}>
                                {index + 1}°
                              </Badge>
                              <div className="flex flex-col">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 w-6 p-0"
                                  disabled={index === 0}
                                  onClick={() => {
                                    const newLocations = [...preferredLocations];
                                    const currentIdx = newLocations.findIndex(l => l.id === location.id);
                                    if (currentIdx > 0) {
                                      // Swap priorities
                                      const temp = newLocations[currentIdx].priority;
                                      newLocations[currentIdx].priority = newLocations[currentIdx - 1].priority;
                                      newLocations[currentIdx - 1].priority = temp;
                                      setPreferredLocations(newLocations);
                                    }
                                  }}
                                >
                                  <ChevronUp className="w-4 h-4" />
                                </Button>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 w-6 p-0"
                                  disabled={index === preferredLocations.length - 1}
                                  onClick={() => {
                                    const newLocations = [...preferredLocations];
                                    const currentIdx = newLocations.findIndex(l => l.id === location.id);
                                    if (currentIdx < newLocations.length - 1) {
                                      // Swap priorities
                                      const temp = newLocations[currentIdx].priority;
                                      newLocations[currentIdx].priority = newLocations[currentIdx + 1].priority;
                                      newLocations[currentIdx + 1].priority = temp;
                                      setPreferredLocations(newLocations);
                                    }
                                  }}
                                >
                                  <ChevronDown className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>

                            {/* Contenido de la ubicación */}
                            <div className="flex-1 space-y-3">
                              <div className="grid md:grid-cols-2 gap-3">
                                {/* Tipo de ubicación */}
                                <div>
                                  <Label className="text-xs text-slate-500 mb-1">Tipo de lugar</Label>
                                  <Select 
                                    value={location.type}
                                    onValueChange={(value) => {
                                      setPreferredLocations(preferredLocations.map(l => 
                                        l.id === location.id ? { ...l, type: value } : l
                                      ));
                                    }}
                                  >
                                    <SelectTrigger className="border-slate-200" data-testid={`location-type-${index}`}>
                                      <SelectValue placeholder="Selecciona tipo" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {locationTypes.map((type) => (
                                        <SelectItem key={type.value} value={type.value}>
                                          <span className="flex items-center">
                                            <type.icon className={`w-4 h-4 mr-2 ${type.color}`} />
                                            {type.label}
                                          </span>
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>

                                {/* Nombre/Referencia */}
                                <div>
                                  <Label className="text-xs text-slate-500 mb-1">Nombre o referencia</Label>
                                  <Input
                                    value={location.name}
                                    onChange={(e) => {
                                      setPreferredLocations(preferredLocations.map(l => 
                                        l.id === location.id ? { ...l, name: e.target.value } : l
                                      ));
                                    }}
                                    placeholder="Ej: Mi casa, Oficina central..."
                                    className="border-slate-200"
                                    data-testid={`location-name-${index}`}
                                  />
                                </div>
                              </div>

                              {/* Dirección completa con autocompletado y mapa */}
                              <div>
                                <Label className="text-xs text-slate-500 mb-1">Dirección</Label>
                                <LocationAutocomplete
                                  value={location.locationData}
                                  onChange={(locationData) => {
                                    setPreferredLocations(preferredLocations.map(l => 
                                      l.id === location.id ? { 
                                        ...l, 
                                        address: locationData?.address || '',
                                        locationData: locationData,
                                        lat: locationData?.lat,
                                        lng: locationData?.lng
                                      } : l
                                    ));
                                  }}
                                  placeholder="Buscar ubicación en Costa Rica..."
                                  required={index === 0}
                                />
                                
                                {/* Mini mapa de la ubicación seleccionada */}
                                {location.lat && location.lng && (
                                  <div className="mt-2">
                                    <LocationMap
                                      position={{
                                        lat: location.lat,
                                        lng: location.lng,
                                        address: location.address
                                      }}
                                      height="120px"
                                      zoom={15}
                                      popupText={location.name || location.address}
                                      interactive={false}
                                    />
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Botón eliminar */}
                            {preferredLocations.length > 1 && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  const filtered = preferredLocations.filter(l => l.id !== location.id);
                                  // Reordenar prioridades
                                  const reordered = filtered.map((l, idx) => ({ ...l, priority: idx + 1 }));
                                  setPreferredLocations(reordered);
                                }}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                data-testid={`remove-location-btn-${index}`}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            )}
                          </div>

                          {/* Indicador de prioridad */}
                          {index === 0 && (
                            <div className="mt-2 flex items-center text-xs text-yellow-700">
                              <span className="bg-yellow-100 px-2 py-0.5 rounded">⭐ Ubicación principal</span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                </div>

                {/* Nota sobre ubicaciones */}
                <p className="text-xs text-slate-500 mt-3 flex items-center">
                  <AlertCircle className="w-3 h-3 mr-1" />
                  La primera ubicación será tu preferencia principal. Los talleres verán todas las opciones.
                </p>
              </div>

              {/* Nota informativa */}
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <p className="text-sm text-purple-800">
                  <strong>📝 Nota:</strong> Tu solicitud estará visible para talleres cercanos durante 7 días. 
                  Recibirás notificaciones cuando los talleres envíen ofertas. Las fechas confirmadas 
                  ayudan a los talleres a organizar mejor su disponibilidad.
                </p>
              </div>

              {/* Botón de envío */}
              <Button 
                type="submit" 
                className={`w-full ${
                  hasConfirmedDate 
                    ? 'bg-purple-600 hover:bg-purple-700' 
                    : 'bg-gray-400 cursor-not-allowed'
                }`}
                size="lg" 
                disabled={!hasConfirmedDate}
                data-testid="submit-request-btn"
              >
                {hasConfirmedDate ? (
                  <>
                    <CheckCircle className="w-5 h-5 mr-2" />
                    Publicar Solicitud
                  </>
                ) : (
                  <>
                    <AlertCircle className="w-5 h-5 mr-2" />
                    Confirma al menos una fecha
                  </>
                )}
              </Button>
            </form>
          )}
        </Card>
      </div>
    </div>
  );
};

export default ServiceRequestForm;
